<template>
    <div class="debug-fryers">
        <h3>Debug - Fryers</h3>
        
        <div class="debug-section">
            <h4>Estado de Freidoras ({{ fryers.length }})</h4>
            <div v-for="fryer in fryers" :key="fryer.id" class="debug-item">
                <strong>Freidora {{ fryer.id }}</strong>
                <div class="fryer-status">
                    <span :class="getFryerStatusClass(fryer.status)">
                        {{ getFryerStatusText(fryer.status) }}
                    </span>
                </div>
            </div>
        </div>
        
        <div class="debug-section">
            <h4>Resumen de Estados</h4>
            <div class="debug-summary">
                <div class="summary-item">
                    <span class="label">Disponibles:</span>
                    <span class="value available">{{ availableFryersCount }}</span>
                </div>
                <div class="summary-item">
                    <span class="label">En uso:</span>
                    <span class="value busy">{{ busyFryersCount }}</span>
                </div>
                <div class="summary-item">
                    <span class="label">Deshabilitadas:</span>
                    <span class="value disabled">{{ disabledFryersCount }}</span>
                </div>
            </div>
        </div>
        
        <div class="debug-section" v-if="productsCooking.length > 0">
            <h4>Productos Cocinando ({{ productsCooking.length }})</h4>
            <div v-for="product in productsCooking" :key="`${product.id}-${product.fryerId}`" class="debug-item">
                <strong>{{ product.name }}</strong>
                <div class="product-info">
                    <span>Freidora: {{ product.fryerId }}</span>
                    <span>Unidades: {{ product.currentUnits }}</span>
                    <span>Tiempo: {{ product.currentTime }} min</span>
                    <span>Inicio: {{ formatTime(product.startTime) }}</span>
                    <span>Fin estimado: {{ formatTime(product.estimatedEndTime) }}</span>
                    <span :class="getTimeRemainingClass(product.estimatedEndTime)">
                        {{ getTimeRemaining(product.estimatedEndTime) }}
                    </span>
                </div>
            </div>
        </div>
        
        <div class="debug-section">
            <h4>Información del Sistema</h4>
            <div class="debug-info">
                <p><strong>Hora actual:</strong> {{ currentTime }}</p>
                <p><strong>Total freidoras:</strong> {{ fryers.length }}</p>
                <p><strong>Productos cocinando:</strong> {{ productsCooking.length }}</p>
                <p><strong>Próxima liberación:</strong> {{ nextReleaseTime }}</p>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DebugFryers',
    
    props: {
        fryers: {
            type: Array,
            default: () => []
        },
        productsCooking: {
            type: Array,
            default: () => []
        }
    },
    
    computed: {
        availableFryersCount() {
            return this.fryers.filter(fryer => fryer.status === 1).length
        },
        
        busyFryersCount() {
            return this.fryers.filter(fryer => fryer.status === 2).length
        },
        
        disabledFryersCount() {
            return this.fryers.filter(fryer => fryer.status === -1).length
        },
        
        currentTime() {
            return moment().format('HH:mm:ss')
        },
        
        nextReleaseTime() {
            if (this.productsCooking.length === 0) {
                return 'N/A'
            }
            
            const sortedProducts = [...this.productsCooking].sort((a, b) => {
                return moment(a.estimatedEndTime).diff(moment(b.estimatedEndTime))
            })
            
            const nextProduct = sortedProducts[0]
            return moment(nextProduct.estimatedEndTime).format('HH:mm:ss')
        }
    },
    
    methods: {
        getFryerStatusText(status) {
            switch (status) {
                case -1: return 'Deshabilitada'
                case 1: return 'Disponible'
                case 2: return 'En uso'
                default: return 'Desconocido'
            }
        },
        
        getFryerStatusClass(status) {
            switch (status) {
                case -1: return 'status-disabled'
                case 1: return 'status-available'
                case 2: return 'status-busy'
                default: return 'status-unknown'
            }
        },
        
        formatTime(timeString) {
            if (!timeString) return 'N/A'
            return moment(timeString).format('HH:mm:ss')
        },
        
        getTimeRemaining(endTime) {
            if (!endTime) return 'N/A'
            
            const now = moment()
            const end = moment(endTime)
            const diff = end.diff(now, 'minutes')
            
            if (diff <= 0) {
                return 'Completado'
            } else if (diff < 60) {
                return `${diff} min`
            } else {
                const hours = Math.floor(diff / 60)
                const minutes = diff % 60
                return `${hours}h ${minutes}min`
            }
        },
        
        getTimeRemainingClass(endTime) {
            if (!endTime) return 'time-unknown'
            
            const now = moment()
            const end = moment(endTime)
            const diff = end.diff(now, 'minutes')
            
            if (diff <= 0) {
                return 'time-completed'
            } else if (diff <= 5) {
                return 'time-critical'
            } else if (diff <= 15) {
                return 'time-warning'
            } else {
                return 'time-normal'
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.debug-fryers {
    font-family: monospace;
    font-size: 12px;
    line-height: 1.4;
    
    h3 {
        color: #e74c3c;
        margin-bottom: 16px;
        border-bottom: 1px solid #ddd;
        padding-bottom: 8px;
    }
    
    .debug-section {
        margin-bottom: 20px;
        
        h4 {
            color: #3498db;
            margin-bottom: 8px;
            font-size: 14px;
        }
    }
    
    .debug-item {
        margin-bottom: 8px;
        padding: 8px;
        background-color: #f8f9fa;
        border-radius: 4px;
        border-left: 3px solid #007bff;
        
        strong {
            color: #2c3e50;
        }
    }
    
    .fryer-status {
        margin-top: 4px;
        margin-left: 16px;
        
        span {
            display: inline-block;
            padding: 2px 8px;
            border-radius: 3px;
            font-size: 11px;
            font-weight: bold;
            
            &.status-available {
                background-color: #d4edda;
                color: #155724;
            }
            
            &.status-busy {
                background-color: #f8d7da;
                color: #721c24;
            }
            
            &.status-disabled {
                background-color: #e2e3e5;
                color: #6c757d;
            }
            
            &.status-unknown {
                background-color: #fff3cd;
                color: #856404;
            }
        }
    }
    
    .debug-summary {
        .summary-item {
            display: flex;
            justify-content: space-between;
            margin-bottom: 4px;
            
            .label {
                color: #495057;
                font-weight: bold;
            }
            
            .value {
                padding: 2px 8px;
                border-radius: 3px;
                font-weight: bold;
                
                &.available {
                    background-color: #d4edda;
                    color: #155724;
                }
                
                &.busy {
                    background-color: #f8d7da;
                    color: #721c24;
                }
                
                &.disabled {
                    background-color: #e2e3e5;
                    color: #6c757d;
                }
            }
        }
    }
    
    .product-info {
        margin-top: 4px;
        margin-left: 16px;
        
        span {
            display: inline-block;
            margin-right: 12px;
            font-size: 11px;
            color: #495057;
            background-color: #f8f9fa;
            padding: 2px 6px;
            border-radius: 3px;
            margin-bottom: 2px;
        }
    }
    
    .debug-info {
        p {
            margin-bottom: 4px;
            color: #495057;
            
            strong {
                color: #2c3e50;
            }
        }
    }
    
    .time-completed {
        background-color: #d4edda !important;
        color: #155724 !important;
    }
    
    .time-critical {
        background-color: #f8d7da !important;
        color: #721c24 !important;
    }
    
    .time-warning {
        background-color: #fff3cd !important;
        color: #856404 !important;
    }
    
    .time-normal {
        background-color: #d1ecf1 !important;
        color: #0c5460 !important;
    }
    
    .time-unknown {
        background-color: #e2e3e5 !important;
        color: #6c757d !important;
    }
}
</style>

